// Remove a Wi-Fi network from a device.
//
// Ports wifiHandler.dart removeWifiPopup: list what the device is holding, tap
// one, confirm, publish REMOVEWIFI.
//
// The list comes from the device itself. `getallwifi` makes it report every
// network it holds on its /client topic — there is no server copy to read, and
// removing a network the device does not have would be a no-op the user could
// not tell apart from a failure.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  List, ListItemButton, ListItemText, ListItemIcon, CircularProgress, useTheme,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useApp } from '../../../core/state/AppContext';
import { publishCommand } from '../../../core/utils/deviceController';
import { connectMqtt, subscribeTopic, addMessageListener } from '../../../core/mqtt/mqttClient';
import { CMD_GET_ALL_WIFI, buildRemoveWifiMqtt, parseWifiList } from '../../../core/devices/commands';

// Networks the firmware falls back to. Removing one can strand a device with no
// way back onto the network, so the Dart refuses and so does this.
const PROTECTED = ['home', 'smartify'];

export default function WifiRemoveDialog({ open, uno, onClose }) {
  const theme = useTheme();
  const c = theme.sli;
  const { userId } = useApp();

  const [list, setList] = useState(null);   // null = still waiting
  const [confirm, setConfirm] = useState(null);
  const [blocked, setBlocked] = useState('');

  useEffect(() => {
    if (!open || !uno) return undefined;
    setList(null);
    setConfirm(null);
    setBlocked('');

    connectMqtt();
    subscribeTopic(`${uno}/client`);

    // Listen before asking: the reply can arrive in milliseconds.
    const detach = addMessageListener((topic, payload) => {
      if (topic !== `${uno}/client`) return;
      const body = String(payload).replace(/^\[[^\]]*\]/, '');
      if (!body.includes(',')) return;      // a status frame, not a Wi-Fi list
      const parsed = parseWifiList(body).filter((w) => w.ssid);
      if (parsed.length) setList(parsed);
    });

    publishCommand(uno, CMD_GET_ALL_WIFI, userId);
    // A device that never answers leaves an honest empty list rather than a
    // spinner that never stops.
    const t = setTimeout(() => setList((prev) => (prev === null ? [] : prev)), 6000);

    return () => { detach(); clearTimeout(t); };
  }, [open, uno, userId]);

  const tap = (w) => {
    if (PROTECTED.includes(String(w.ssid).trim().toLowerCase())) {
      setBlocked(w.ssid);
      return;
    }
    setConfirm(w);
  };

  const doRemove = () => {
    publishCommand(uno, buildRemoveWifiMqtt(confirm.ssid, confirm.pass), userId);
    setList((prev) => (prev || []).filter((w) => w.ssid !== confirm.ssid));
    setConfirm(null);
  };

  const paper = { bgcolor: c.back, borderRadius: '20px', width: 'min(92vw, 420px)' };

  return (
    <>
      <Dialog open={open} onClose={onClose} PaperProps={{ sx: paper }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700, fontSize: 18 }}>
          Connected Wi-Fi
        </DialogTitle>
        <DialogContent>
          {list === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={18} sx={{ color: c.text }} />
              <Typography sx={{ color: c.text }}>Asking the device…</Typography>
            </Box>
          ) : list.length === 0 ? (
            <Typography sx={{ color: `${c.text}B3` }}>
              The device did not answer. It may be offline — try again once it is
              back online.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {list.map((w) => (
                <ListItemButton key={w.ssid} onClick={() => tap(w)} sx={{ borderRadius: '12px' }}>
                  <ListItemIcon sx={{ color: c.text, minWidth: 34 }}><WifiIcon /></ListItemIcon>
                  <ListItemText
                    primary={w.ssid}
                    primaryTypographyProps={{ sx: { color: c.text, fontWeight: 600 } }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
          {blocked && (
            <Typography sx={{ color: '#F44336', fontSize: 13, mt: 1 }}>
              {`"${blocked}" is a fallback network — removing it could leave the `}
              device with no way back online.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: c.text }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} PaperProps={{ sx: paper }}>
        <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: '#F44336' }} />
          <Typography sx={{ color: c.text, fontSize: 20, fontWeight: 700, mt: 1 }}>
            Remove Wi-Fi?
          </Typography>
          <Typography sx={{ color: `${c.text}B3`, mt: 1 }}>
            {`Remove "${confirm?.ssid}" from this device?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} sx={{ color: c.text }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={doRemove}
            sx={{ bgcolor: '#F44336', color: '#fff', borderRadius: '12px' }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
